/**
 * The simple view's conversation with its "In this conversation" column docked beside it (owner 2026-10-31: resizable,
 * collapsible, remembering its width). On a wide window the two share a `SplitPane`; below 1100px, where a second
 * column does not fit, the column is a `Sheet` over the conversation. Demo page: /chats (thread with its context
 * column).
 */
import type React from 'react';
import { ConversationEntitiesColumn } from '../../components/conversations/entities/ConversationEntitiesColumn.js';
import { Sheet } from '../../components/ui/Sheet.js';
import { SplitPane } from '../../components/ui/SplitPane.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useSimpleRecordsColumn } from './simple-records-state.js';

interface SimpleRecordsLayoutProps {
  conversationId: string | null;
  newestMessageId: string | null;
  /** The conversation itself. */
  children: React.ReactNode;
}

export const SimpleRecordsLayout: React.FC<SimpleRecordsLayoutProps> = ({
  conversationId,
  newestMessageId,
  children,
}) => {
  const wide = useMediaQuery('(min-width: 1100px)');
  const column = useSimpleRecordsColumn();
  if (!conversationId || !column.visible) return <>{children}</>;

  if (wide) {
    return (
      <SplitPane
        storageKey="simple.conversation-records"
        defaultLeft={62}
        minLeft={35}
        maxLeft={80}
        handleLabel="Resize the conversation and what it works on"
        className="h-full min-h-0"
        left={children}
        right={
          <ConversationEntitiesColumn
            conversationId={conversationId}
            newestMessageId={newestMessageId}
            onClose={column.close}
          />
        }
      />
    );
  }

  return (
    <>
      {children}
      <Sheet open onClose={column.close} title="In this conversation" width="lg">
        <ConversationEntitiesColumn
          conversationId={conversationId}
          newestMessageId={newestMessageId}
          onClose={column.close}
          showHeader={false}
        />
      </Sheet>
    </>
  );
};
